/**
 * Util function to derive a generic view context attribute for analytics.
 *
 * Reads the AI feature context (populated by product-specific publishers, e.g. the Jira
 * Issue Navigator and Backlog views) and returns a flat record of scalar values that can
 * be attached to unified AI events (e.g. aiInteraction initiated) without leaking any
 * product-specific nested shape.
 *
 * Returns `undefined` when no known view context is available, in which case no
 * `viewContext` attribute is attached to the event.
 *
 * This is exported only for testing purposes.
 */
export const getViewContext = ({
	aiFeatureContext,
}: {
	aiFeatureContext?: Record<string, unknown> | null;
}): Record<string, boolean | number | string> | undefined => {
	if (!aiFeatureContext?.jira_view_context) {
		return undefined;
	}
	const jiraCtx = aiFeatureContext.jira_view_context as Record<string, unknown>;
	const ids = jiraCtx.selectedIssueIdentifiers;
	const projects = jiraCtx.projectIdentifiers;
	const filters = jiraCtx.filterIdentifiers;
	const jqlStrings = jiraCtx.jqlStrings;
	return {
		numberOfWorkItems: Array.isArray(ids) ? ids.length : 0,
		numberOfProjects: Array.isArray(projects) ? projects.length : 0,
		numberOfFilters: Array.isArray(filters) ? filters.length : 0,
		numberOfJqlStrings: Array.isArray(jqlStrings) ? jqlStrings.length : 0,
		viewType: typeof jiraCtx.viewType === 'string' ? jiraCtx.viewType : 'unknown',
	};
};
